import React from "react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { Plus, X, UserPlus, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

/**
 * Dialog for creating a new lesson from the calendar.
 * Includes student search and inline student creation.
 */
const LessonFormDialog = ({
  open,
  onOpenChange,
  selectedDate,
  lessonForm,
  setLessonForm,
  // Student search
  lessonStudentSearch,
  setLessonStudentSearch,
  lessonStudentResults,
  lessonSelectedStudents,
  setLessonSelectedStudents,
  setLessonStudentResults,
  // Inline student creation
  showInlineStudentCreate,
  setShowInlineStudentCreate,
  inlineStudent,
  setInlineStudent,
  onInlineCreateStudent,
  // Submit
  onCreateLesson
}) => {
  const handleSelectStudent = (student) => {
    setLessonSelectedStudents(prev => [...prev, student]);
    setLessonStudentSearch("");
    setLessonStudentResults([]);
  };

  const handleRemoveStudent = (userId) => {
    setLessonSelectedStudents(prev => prev.filter(x => x.user_id !== userId));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Nova Aula — {selectedDate && format(parseISO(selectedDate), "d MMM yyyy", { locale: pt })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Category and Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1">Modalidade</label>
              <select 
                value={lessonForm.lesson_category} 
                onChange={(e) => setLessonForm({...lessonForm, lesson_category: e.target.value})} 
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" 
                data-testid="lesson-category"
              >
                <option value="surf">Surf</option>
                <option value="bodyboard">Bodyboard</option>
                <option value="both">Ambos</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1">Tipo</label>
              <select 
                value={lessonForm.lesson_type} 
                onChange={(e) => setLessonForm({...lessonForm, lesson_type: e.target.value})} 
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" 
                data-testid="lesson-type"
              >
                <option value="group">Grupo</option>
                <option value="private">Privada</option>
              </select>
            </div>
          </div>

          {/* Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1">Início</label>
              <Input 
                type="time" 
                value={lessonForm.start_time} 
                onChange={(e) => setLessonForm({...lessonForm, start_time: e.target.value})} 
                data-testid="lesson-start-time" 
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1">Fim</label>
              <Input 
                type="time" 
                value={lessonForm.end_time} 
                onChange={(e) => setLessonForm({...lessonForm, end_time: e.target.value})} 
                data-testid="lesson-end-time" 
              />
            </div>
          </div>

          {/* Max students and Price */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1">Max. alunos</label>
              <Input 
                type="number" 
                min="1" 
                value={lessonForm.max_students} 
                onChange={(e) => setLessonForm({...lessonForm, max_students: e.target.value === '' ? '' : parseInt(e.target.value)})} 
                onBlur={(e) => setLessonForm({...lessonForm, max_students: parseInt(e.target.value) || 1})} 
                data-testid="lesson-max-students" 
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1">Preço (€)</label>
              <Input 
                type="number" 
                min="0" 
                step="0.5" 
                value={lessonForm.price} 
                onChange={(e) => setLessonForm({...lessonForm, price: e.target.value === '' ? '' : parseFloat(e.target.value)})} 
                onBlur={(e) => setLessonForm({...lessonForm, price: parseFloat(e.target.value) || 0})} 
                data-testid="lesson-price" 
              />
            </div>
          </div>

          {/* Student Association */}
          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[#475569]">Alunos a inscrever</label>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs text-[#6697a4]" 
                onClick={() => setShowInlineStudentCreate(!showInlineStudentCreate)} 
                data-testid="lesson-toggle-create-student"
              >
                <UserPlus className="h-3 w-3 mr-1" /> Criar Novo
              </Button>
            </div>

            {/* Inline create student */}
            {showInlineStudentCreate && (
              <div className="mb-3 p-3 bg-slate-50 rounded-lg space-y-2">
                <Input 
                  placeholder="Nome *" 
                  value={inlineStudent.name} 
                  onChange={(e) => setInlineStudent({...inlineStudent, name: e.target.value})} 
                  className="h-8 text-sm" 
                  data-testid="inline-student-name" 
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input 
                    placeholder="Email (opcional)" 
                    value={inlineStudent.email} 
                    onChange={(e) => setInlineStudent({...inlineStudent, email: e.target.value})} 
                    className="h-8 text-sm" 
                    data-testid="inline-student-email" 
                  />
                  <Input 
                    placeholder="Telefone (opcional)" 
                    value={inlineStudent.phone} 
                    onChange={(e) => setInlineStudent({...inlineStudent, phone: e.target.value})} 
                    className="h-8 text-sm" 
                    data-testid="inline-student-phone" 
                  />
                </div>
                <Button 
                  size="sm" 
                  onClick={onInlineCreateStudent} 
                  className="bg-[#6697a4] hover:bg-[#567f8a] h-7 text-xs" 
                  data-testid="inline-create-student-btn"
                >
                  Criar e Adicionar
                </Button>
              </div>
            )}

            {/* Search existing students */}
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
              <Input
                placeholder="Pesquisar aluno existente..."
                value={lessonStudentSearch}
                onChange={(e) => setLessonStudentSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="lesson-student-search"
              />
            </div>

            {/* Search results */}
            {lessonStudentResults.length > 0 && (
              <div className="space-y-1 max-h-32 overflow-y-auto mb-2">
                {lessonStudentResults.map(s => (
                  <button 
                    key={s.user_id} 
                    onClick={() => handleSelectStudent(s)}
                    className="w-full flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 text-left text-sm" 
                    data-testid={`lesson-add-student-${s.user_id}`}
                  >
                    <span className="w-6 h-6 rounded-full bg-[#6697a4] text-white text-[10px] flex items-center justify-center shrink-0">
                      {s.name?.charAt(0)}
                    </span>
                    <span className="truncate flex-1">{s.name || s.email}</span>
                    <Plus className="h-3.5 w-3.5 text-[#6697a4] shrink-0" />
                  </button>
                ))}
              </div>
            )}

            {/* Selected students */}
            {lessonSelectedStudents.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {lessonSelectedStudents.map(s => (
                  <Badge 
                    key={s.user_id} 
                    variant="secondary" 
                    className="pl-2 pr-1 py-1 gap-1" 
                    data-testid={`lesson-selected-student-${s.user_id}`}
                  >
                    {s.name}
                    <button 
                      onClick={() => handleRemoveStudent(s.user_id)} 
                      className="ml-0.5 hover:bg-slate-200 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onCreateLesson} className="bg-[#6697a4] hover:bg-[#567f8a]" data-testid="save-lesson-btn">
            Criar Aula {lessonSelectedStudents.length > 0 ? `(+${lessonSelectedStudents.length} aluno${lessonSelectedStudents.length > 1 ? "s" : ""})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LessonFormDialog;
